import React from 'react';
import {
  Dialog, Box, Stack, Typography, Button, IconButton,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { useSuccessPopup } from './successPopupShared';
import { UPLOAD_BLOCKER_FEATURES } from './uploadBlockerShared';

// New-UI (v2) presentation of the post-purchase success popup. Matches the v2
// design language: Montserrat, #15171c ink, white rounded card, dark primary
// button. All behaviour comes from useSuccessPopup so it stays identical to the
// classic variant.

const FONT = 'Montserrat, sans-serif';
const DARK_BTN = {
  bgcolor: '#15171c',
  color: '#fff',
  textTransform: 'none',
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: 15,
  borderRadius: 2,
  py: 1.25,
  '&:hover': { bgcolor: '#000' },
};

// Keep the post-purchase benefit list in lockstep with the upgrade/blocker popup.
const FEATURES = UPLOAD_BLOCKER_FEATURES;

function SuccessPopUpV2({
  expressData,
  onHide,
  paymentStatus,
  paymentNavigation,
  setOpenPaymentLoader,
  isGift = false,
  isSwitchSuccess = false,
}) {
  const {
    isSuccess, isTrial, endDate, amountText, showRenewal, handleClose, handleRetry,
  } = useSuccessPopup({
    expressData, onHide, paymentStatus, paymentNavigation, setOpenPaymentLoader, isSwitchSuccess,
  });

  const planName = expressData?.plan_name;
  const primaryLabel = paymentStatus === 'affliate' || isGift ? 'Done' : 'Continue';

  const trialNote = isTrial && endDate ? `Trial ends on ${endDate}` : '';

  return (
    <Dialog
      open
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: 3,
          width: '100%',
          maxWidth: 420,
          m: 2,
        },
      }}
    >
      <Box sx={{
        position: 'relative', p: '32px 28px 28px', fontFamily: FONT, textAlign: 'center',
      }}
      >
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="Close"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            bgcolor: 'rgba(21,23,28,0.05)',
            '&:hover': { bgcolor: 'rgba(21,23,28,0.1)' },
          }}
        >
          <CloseRoundedIcon fontSize="small" sx={{ color: '#475467' }} />
        </IconButton>

        <Box sx={{
          width: 64,
          height: 64,
          mx: 'auto',
          mb: 2.25,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isSuccess ? '#e8f7ee' : '#fdecec',
        }}
        >
          {isSuccess
            ? <CheckCircleRoundedIcon sx={{ fontSize: 40, color: '#1aa251' }} />
            : <ErrorOutlineRoundedIcon sx={{ fontSize: 40, color: '#e3220a' }} />}
        </Box>

        {isSuccess ? (
          <>
            <Typography sx={{
              fontFamily: FONT, fontWeight: 800, fontSize: 22, color: '#15171c', mb: 0.5,
            }}
            >
              {/* eslint-disable-next-line no-nested-ternary */}
              {isGift ? 'Gift sent successfully' : isTrial ? 'Trial Activated' : 'Payment successful'}
            </Typography>
            <Typography sx={{
              fontFamily: FONT, fontSize: 14, color: '#5b6472', mb: 2.5, lineHeight: 1.5,
            }}
            >
              {isGift ? 'You gifted the ' : "You're on the "}
              <Box component="span" sx={{ fontWeight: 700, color: '#15171c' }}>{planName || 'new'}</Box>
              {' plan.'}
              <br />
              {isGift ? 'Your recipient now has access to:' : 'You now have access to:'}
            </Typography>

            <Stack
              spacing={1.25}
              sx={{
                textAlign: 'left',
                borderTop: '1px solid #eef0f3',
                borderBottom: '1px solid #eef0f3',
                py: 2,
                mb: 2,
              }}
            >
              {FEATURES.map((f) => (
                <Stack key={f} direction="row" alignItems="center" spacing={1.25}>
                  <CheckRoundedIcon sx={{ fontSize: 18, color: '#1aa251' }} />
                  <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#2b303b' }}>{f}</Typography>
                </Stack>
              ))}
            </Stack>

            {isTrial ? (
              trialNote && (
                <Typography sx={{
                  fontFamily: FONT, fontSize: 14, fontWeight: 600, color: '#15171c', textAlign: 'center', mb: 2.75,
                }}
                >
                  {trialNote}
                </Typography>
              )
            ) : (
              (amountText || (showRenewal && endDate)) && (
                <Stack spacing={0.75} sx={{ mb: 2.75 }}>
                  {amountText && (
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#6b7280' }}>Amount paid</Typography>
                      <Typography sx={{
                        fontFamily: FONT, fontSize: 13, fontWeight: 700, color: '#15171c',
                      }}
                      >
                        {amountText}
                      </Typography>
                    </Stack>
                  )}
                  {showRenewal && endDate && (
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#6b7280' }}>Next renewal</Typography>
                      <Typography sx={{
                        fontFamily: FONT, fontSize: 13, fontWeight: 700, color: '#15171c',
                      }}
                      >
                        {endDate}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              )
            )}

            <Button fullWidth disableElevation variant="contained" sx={DARK_BTN} onClick={handleClose}>
              {primaryLabel}
            </Button>
          </>
        ) : (
          <>
            <Typography sx={{
              fontFamily: FONT, fontWeight: 800, fontSize: 22, color: '#15171c', mb: 0.5,
            }}
            >
              Payment unsuccessful
            </Typography>
            <Typography sx={{
              fontFamily: FONT, fontSize: 14, color: '#5b6472', mb: 3, lineHeight: 1.5,
            }}
            >
              Your purchase didn&apos;t go through. No charge was made — please try again.
            </Typography>
            <Button fullWidth disableElevation variant="contained" sx={DARK_BTN} onClick={handleRetry}>
              Retry your purchase
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
}

export default SuccessPopUpV2;
